// src/adminFlow/AdminNewProductPage.js
import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import API_BASE from '../apiBase';
import { Upload, Image as ImageIcon, ArrowLeft, Home } from 'lucide-react';

// NEW: cross-tab invalidation broadcaster
import { broadcastInvalidation } from './adminInvalidation';

function Field({ label, hint, children, required }) {
  return (
    <div className="space-y-1">
      <label className="text-sm font-medium text-gray-700">
        {label} {required ? <span className="text-red-500">*</span> : null}
      </label>
      {children}
      {hint ? <div className="text-xs text-gray-500">{hint}</div> : null}
    </div>
  );
}

export default function AdminNewProductPage() {
  const nav = useNavigate();

  // product basics
  const [name, setName] = useState('');
  const [brand, setBrand] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [currency, setCurrency] = useState('CAD');

  // dietary flags
  const [isSugarFree, setIsSugarFree] = useState(false);
  const [isVegan, setIsVegan] = useState(false);
  const [isGlutenFree, setIsGlutenFree] = useState(false);
  const [isAllNatural, setIsAllNatural] = useState(false);

  // flavors as comma-separated
  const [flavorsText, setFlavorsText] = useState('');

  // image (either URL or uploaded)
  const [imageUrl, setImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);

  // case-only variant
  const [casePack, setCasePack] = useState('12-pack'); // label
  const [caseUnits, setCaseUnits] = useState('12');    // cans per case
  const [casePrice, setCasePrice] = useState('');
  const [caseSku, setCaseSku] = useState('');
  const [caseStock, setCaseStock] = useState('');

  const [serverError, setServerError] = useState('');
  const [saving, setSaving] = useState(false);

  async function doUpload(file) {
    if (!file) return;
    setServerError('');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const r = await fetch(`${API_BASE}/api/admin/uploads/image`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
        cache: 'no-store',
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'Upload failed');
      setImageUrl(j.absolute || j.url);
    } catch (e) {
      setServerError(e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  async function onSubmit(e) {
    e.preventDefault();
    setServerError('');
    setSaving(true);
    try {
      if (!name.trim()) throw new Error('Name is required');
      if (!casePrice) throw new Error('Case price is required');
      if (!caseSku.trim()) throw new Error('Case SKU is required');

      const payload = {
        name: name.trim(),
        brand: brand.trim() || undefined,
        category: category.trim() || undefined,
        description: description.trim() || undefined,
        currency: currency || 'CAD',
        image_url: imageUrl || undefined,
        flavors: flavorsText
          .split(',')
          .map(s => s.trim())
          .filter(Boolean),

        // dietary flags
        is_sugar_free: !!isSugarFree,
        is_vegan: !!isVegan,
        is_gluten_free: !!isGlutenFree,
        is_all_natural: !!isAllNatural,

        // case-only variant fields the API expects
        case_pack_size: casePack || 'Case',
        case_unit_count: caseUnits || 12,
        case_price: casePrice,
        case_sku: caseSku.trim(),
        case_stock: caseStock || 0,
      };

      const r = await fetch(`${API_BASE}/api/admin/products`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        cache: 'no-store',
        body: JSON.stringify(payload),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'Failed to create product');

      // notify other tabs immediately
      const createdId = j?.product?.id ?? j?.id ?? null;
      broadcastInvalidation('products:changed', createdId ? { productId: createdId } : {});
      broadcastInvalidation('inventory:changed', createdId ? { productId: createdId } : {});

      nav('/admin/products', { replace: true });
    } catch (e) {
      setServerError(e.message || 'Error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">Add New Product</h1>
          <div className="flex items-center gap-2">
            <Link
              to="/admin"
              className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
              title="Go to dashboard"
            >
              <Home className="w-4 h-4" />
              <span className="hidden sm:inline">Dashboard</span>
            </Link>
            <Link
              to="/admin/products"
              className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800"
              title="Back to inventory"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Inventory
            </Link>
          </div>
        </div>

        {serverError ? (
          <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 text-red-800 border border-red-100">
            {serverError}
          </div>
        ) : null}

        <form onSubmit={onSubmit} className="space-y-6">
          {/* Details */}
          <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Name" required>
                <input
                  value={name}
                  onChange={(e)=>setName(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Classic Coca-Cola"
                />
              </Field>

              <Field label="Brand" hint="Create if not found">
                <input
                  value={brand}
                  onChange={(e)=>setBrand(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Coca-Cola"
                />
              </Field>

              <Field label="Category" hint="Create if not found">
                <input
                  value={category}
                  onChange={(e)=>setCategory(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Soda"
                />
              </Field>

              <Field label="Currency">
                <input
                  value={currency}
                  onChange={(e)=>setCurrency(e.target.value.toUpperCase())}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="CAD"
                />
              </Field>

              <div className="md:col-span-2">
                <Field label="Description">
                  <textarea
                    value={description}
                    onChange={(e)=>setDescription(e.target.value)}
                    rows={3}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="The classic taste of Coca-Cola."
                  />
                </Field>
              </div>

              {/* Dietary & Claims */}
              <div className="md:col-span-2">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Dietary & Claims</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <label className="inline-flex items-center gap-2 p-2 rounded hover:bg-gray-50 border border-gray-200">
                    <input
                      type="checkbox"
                      checked={isSugarFree}
                      onChange={(e)=>setIsSugarFree(e.target.checked)}
                    />
                    <span className="text-sm text-gray-700">Sugar-Free</span>
                  </label>
                  <label className="inline-flex items-center gap-2 p-2 rounded hover:bg-gray-50 border border-gray-200">
                    <input
                      type="checkbox"
                      checked={isVegan}
                      onChange={(e)=>setIsVegan(e.target.checked)}
                    />
                    <span className="text-sm text-gray-700">Vegan</span>
                  </label>
                  <label className="inline-flex items-center gap-2 p-2 rounded hover:bg-gray-50 border border-gray-200">
                    <input
                      type="checkbox"
                      checked={isGlutenFree}
                      onChange={(e)=>setIsGlutenFree(e.target.checked)}
                    />
                    <span className="text-sm text-gray-700">Gluten-Free</span>
                  </label>
                  <label className="inline-flex items-center gap-2 p-2 rounded hover:bg-gray-50 border border-gray-200">
                    <input
                      type="checkbox"
                      checked={isAllNatural}
                      onChange={(e)=>setIsAllNatural(e.target.checked)}
                    />
                    <span className="text-sm text-gray-700">All-Natural</span>
                  </label>
                </div>
              </div>

              <Field label="Flavors" hint="Comma-separated: Cola, Cherry">
                <input
                  value={flavorsText}
                  onChange={(e)=>setFlavorsText(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Cola, Cherry"
                />
              </Field>
            </div>
          </section>

          {/* Image */}
          <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <h3 className="font-semibold mb-3 text-gray-800">Product Image</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
              <div className="md:col-span-2 space-y-3">
                <Field label="Image URL" hint="Paste a public URL or use Upload below">
                  <input
                    value={imageUrl}
                    onChange={(e)=>setImageUrl(e.target.value)}
                    placeholder="https://…"
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </Field>

                <div className="flex items-center gap-3">
                  <label className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 cursor-pointer">
                    <Upload className="w-4 h-4" />
                    <span>{uploading ? 'Uploading…' : 'Upload file'}</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e)=>doUpload(e.target.files?.[0])}
                      disabled={uploading}
                    />
                  </label>
                  <div className="text-xs text-gray-500">PNG/JPG/GIF/WEBP up to 5MB</div>
                </div>
              </div>

              <div>
                <div className="text-xs text-gray-500 mb-1">Preview</div>
                <div className="w-48 h-48 border rounded-lg overflow-hidden bg-gray-50 flex items-center justify-center">
                  {imageUrl ? (
                    <img
                      src={imageUrl.startsWith('http') ? imageUrl : `${API_BASE}${imageUrl}`}
                      alt="preview"
                      className="w-full h-full object-cover"
                      onError={(e)=>{ e.currentTarget.src=''; }}
                    />
                  ) : (
                    <ImageIcon className="w-8 h-8 text-gray-300" />
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* Case-only Variant */}
          <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <h3 className="font-semibold mb-3 text-gray-800">Variant (Case)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Case label" hint='e.g. "12-pack"'>
                <input
                  value={casePack}
                  onChange={(e)=>setCasePack(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="12-pack"
                />
              </Field>
              <Field label="Cans per case">
                <input
                  type="number" min="1" step="1"
                  value={caseUnits}
                  onChange={(e)=>setCaseUnits(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="12"
                />
              </Field>
              <Field label="Price" required>
                <input
                  type="number" step="0.01" min="0"
                  value={casePrice}
                  onChange={(e)=>setCasePrice(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="15.99"
                />
              </Field>
              <Field label="Stock (cases)">
                <input
                  type="number" step="1" min="0"
                  value={caseStock}
                  onChange={(e)=>setCaseStock(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="80"
                />
              </Field>
              <div className="md:col-span-2">
                <Field label="SKU" required>
                  <input
                    value={caseSku}
                    onChange={(e)=>setCaseSku(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="COKE-12"
                  />
                </Field>
              </div>
            </div>
          </section>

          <div className="flex items-center justify-end gap-3">
            <Link
              to="/admin/products"
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2 rounded-lg bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-60"
            >
              {saving ? 'Saving…' : 'Create Product'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
